import { Component, OnInit, ViewChild, TemplateRef, AfterViewInit, ElementRef, OnDestroy } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import * as Feather from 'feather-icons';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { ServiceService, ArchiveItem, Address } from './service.service';
import { Router } from '@angular/router';
import { HeaderService } from '../../../services/header/header.service';
import { FournisseurService } from '../../../services/Fournisseur/fournisseur.service';
import { FournisseurEventService } from '../../../services/Fournisseur/fournisseur-event.service';
import { Subscription } from 'rxjs';
import { Fournisseur } from '../../../models/fournisseur';

@Component({
    selector: 'app-fournisseur',
    templateUrl: './fournisseur.component.html',
    styleUrls: ['./fournisseur.component.scss']
})
export class FournisseurComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('importModal', { static: false }) importModal!: TemplateRef<any>;
    @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
    selectedFile: File | null = null;

    activeCount: number = 0;
    archivedCount: number = 0;
    stationId: number = 0;
    userId: number = 0;
    private subscriptions: Subscription[] = [];
    private validCivilities: string[] = ['Homme', 'Femme', 'avant','apres'];
    private validAddressTypes: string[] = ['facturation', 'livraison'];
    private validPhoneTypes: string[] = ['mobile', 'fixe'];

    constructor(
        private modalService: NgbModal,
        private toastr: ToastrService,
        private router: Router,
        private headerService: HeaderService,
        private fournisseurService: FournisseurService,
        private fournisseurEventService: FournisseurEventService
    ) {}

    ngOnInit(): void {
        console.log('FournisseurComponent: ngOnInit called');
        if (this.router.url === '/tiers/fournisseurs') {
            this.router.navigate(['/tiers/fournisseurs/actif']);
        }
        this.headerService.setConfig({
            title: 'Fournisseur',
            buttons: [
                {
                    label: 'Exporter',
                    type: 'dropdown',
                    dropdownItems: [
                        { label: 'PDF', action: () => this.exportTable('pdf'), icon: '../../../assets/images/logo/pdf_logo.png' },
                        { label: 'CSV', action: () => this.exportTable('csv'), icon: '../../../assets/images/logo/csv_logo.png' },
                        { label: 'XLSX', action: () => this.exportTable('xlsx'), icon: '../../../assets/images/logo/xlsx_logo.png' }
                    ]
                },
                { label: 'Importer', action: () => this.openImportModal(), type: 'secondary' }
            ]
        });
        this.loadUserData();
        this.loadCounts();
        this.subscribeToEvents();
    }

    ngAfterViewInit() {
        this.refreshFeatherIcons();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    private loadUserData() {
        try {
            const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
            this.stationId = user?.stations?.[0]?.id || 0;
            this.userId = user?.id || 0;
            console.log('loadUserData: stationId=', this.stationId, 'userId=', this.userId);
            if (!this.stationId || !this.userId) {
                this.toastr.error('Utilisateur ou station non valide. Veuillez vous reconnecter.', 'Erreur');
                this.router.navigate(['/login']);
            }
        } catch (error) {
            this.toastr.error('Erreur lors de la récupération des données utilisateur.', 'Erreur');
            console.error('loadUserData: Error=', error);
        }
    }

    private loadCounts() {
        if (!this.stationId) {
            return;
        }
        const activeSub = this.fournisseurService.getActiveFournisseurCount(this.stationId).subscribe({
            next: (count) => {
                this.activeCount = count;
            },
            error: (error) => {
                this.toastr.error('Erreur lors du chargement du nombre de fournisseurs actifs', 'Erreur');
                console.error('loadCounts: Active count error=', error);
            }
        });
        const archivedSub = this.fournisseurService.getArchivedFournisseurCount(this.stationId).subscribe({
            next: (count) => {
                this.archivedCount = count;
            },
            error: (error) => {
                this.toastr.error('Erreur lors du chargement du nombre de fournisseurs archivés', 'Erreur');
                console.error('loadCounts: Archived count error=', error);
            }
        });
        this.subscriptions.push(activeSub, archivedSub);
    }

    private subscribeToEvents() {
        const sub = this.fournisseurEventService.onFournisseurArchiveEvent().subscribe({
            next: ({ fournisseur, is_archive }) => {
                if (is_archive) {
                    this.activeCount--;
                    this.archivedCount++;
                } else {
                    this.activeCount++;
                    this.archivedCount--;
                }
                this.activeCount = Math.max(0, this.activeCount);
                this.archivedCount = Math.max(0, this.archivedCount);
            },
            error: (error) => {
                console.error('subscribeToEvents: Error=', error);
            }
        });
        this.subscriptions.push(sub);
    }

    refreshFeatherIcons() {
        setTimeout(() => {
            Feather.replace();
        }, 0);
    }

    openImportModal() {
        this.selectedFile = null;
        this.modalService.open(this.importModal, { centered: true, size: 'xl' });
        this.refreshFeatherIcons();
    }

    triggerFileInput(event: MouseEvent) {
        event.preventDefault();
        if (this.fileInput?.nativeElement) {
            this.fileInput.nativeElement.click();
        } else {
            this.toastr.error('Erreur d\'accès au sélecteur de fichier', 'Erreur');
        }
    }

    onFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            this.selectedFile = input.files[0];
            if (this.selectedFile.size > 1024 * 1024) {
                this.toastr.error('Fichier trop volumineux (max 1 Mo)', 'Erreur');
                this.selectedFile = null;
                input.value = '';
            } else if (!this.selectedFile.name.endsWith('.xlsx')) {
                this.toastr.error('Seuls les fichiers .xlsx sont autorisés', 'Erreur');
                this.selectedFile = null;
                input.value = '';
            } else {
                this.toastr.success('Fichier sélectionné avec succès', 'Succès');
            }
        } else {
        }
    }

    importFile(modal: any) {
        if (!this.selectedFile) {
            this.toastr.error('Aucun fichier sélectionné', 'Erreur');
            modal.dismiss('No file');
            return;
        }

        if (!this.stationId || !this.userId) {
            this.toastr.error('Utilisateur ou station non valide. Veuillez vous reconnecter.', 'Erreur');
            modal.dismiss('Invalid user');
            return;
        }

        try {
            const reader = new FileReader();
            reader.onload = (e: any) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const firstSheet = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[firstSheet];
                    const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                    const expectedHeaders = [
                        'RÉFÉRENCE',
                        'NOM DU FOURNISSEUR',
                        'EMAIL',
                        'CIVILITÉ',
                        'CHIFFRE D\'AFFAIRES',
                        'MATRICULE FISCAL',
                        'NUMÉROS DE TÉLÉPHONE',
                        'ADRESSES'
                    ];
                    const headers = jsonData[0]?.map((h: any) => h?.toString().trim());
                    if (!headers || !expectedHeaders.every((h, i) => headers[i] === h)) {
                        this.toastr.error('Le fichier doit contenir les colonnes : ' + expectedHeaders.join(', '), 'Erreur');
                        modal.dismiss('Invalid headers');
                        return;
                    }

                    const errors: string[] = [];
                    let importedCount = 0;
                    const totalRows = jsonData.slice(1).length;

                    if (totalRows === 0) {
                        this.toastr.error('Aucune donnée valide trouvée dans le fichier', 'Erreur');
                        this.selectedFile = null;
                        if (this.fileInput?.nativeElement) {
                            this.fileInput.nativeElement.value = '';
                        }
                        modal.dismiss('No data');
                        return;
                    }

                    const dataRows = jsonData.slice(1).filter(row => row.some((cell: any) => cell != null && cell !== ''));
                    if (dataRows.length === 0) {
                        this.toastr.error('Toutes les lignes du fichier sont vides', 'Erreur');
                        this.selectedFile = null;
                        if (this.fileInput?.nativeElement) {
                            this.fileInput.nativeElement.value = '';
                        }
                        modal.dismiss('Empty rows');
                        return;
                    }

                    const existingIds = new Set<string>();
                    for (let i = 0; i < dataRows.length; i++) {
                        const row = dataRows[i];

                        const identifiant_unique = row[0] != null ? row[0].toString().trim() : '';
                        const nom_entreprise = row[1] != null ? row[1].toString().trim() : '';
                        const email = row[2] != null ? row[2].toString().trim() : '';
                        const civilite = row[3] != null ? row[3].toString().trim() : '';
                        const numero_identification_fiscale_raw = row[5] != null ? row[5].toString().trim() : '';
                        let numero_identification_fiscale = numero_identification_fiscale_raw;

                        if (numero_identification_fiscale.includes('E') || numero_identification_fiscale.includes('e')) {
                            try {
                                numero_identification_fiscale = Number(numero_identification_fiscale_raw).toFixed(0);
                            } catch (e) {
                                errors.push(`Ligne ${i + 2} : Matricule fiscal invalide (${numero_identification_fiscale_raw}). Format non numérique.`);
                                continue;
                            }
                        }

                        if (!identifiant_unique) {
                            errors.push(`Ligne ${i + 2} : Référence manquante.`);
                            continue;
                        }
                        if (existingIds.has(identifiant_unique)) {
                            errors.push(`Ligne ${i + 2} : Référence dupliquée (${identifiant_unique}).`);
                            continue;
                        }
                        existingIds.add(identifiant_unique);
                        if (!nom_entreprise) {
                            errors.push(`Ligne ${i + 2} : Nom du fournisseur manquant.`);
                            continue;
                        }
                        if (!email || !/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email)) {
                            errors.push(`Ligne ${i + 2} : Email invalide (${email || 'vide'}).`);
                            continue;
                        }
                        if (!civilite || !this.validCivilities.includes(civilite)) {
                            errors.push(`Ligne ${i + 2} : Civilité invalide (${civilite || 'vide'}). Doit être "Homme", "Femme", "avant" ou "apres".`);
                            continue;
                        }
                        if (numero_identification_fiscale && !/^\d{9}$/.test(numero_identification_fiscale)) {
                            errors.push(`Ligne ${i + 2} : Matricule fiscal invalide (${numero_identification_fiscale || 'vide'}). Doit contenir 9 chiffres.`);
                            continue;
                        }

                        const phoneNumbers = row[6]
                            ? row[6].toString().split(';').map((num: string) => num.trim()).filter((num: string) => num && /^\+?\d{8,}$/.test(num.replace(/^\+\d{1,3}/, '')))
                            : [];
                        const addresses = row[7]
                            ? row[7].toString().split('|').map((addr: string) => {
                                const [addressType, pays, region, codePostal, adresse] = addr.split(',').map((s: string) => s?.trim() || '');
                                return {
                                    type: addressType && this.validAddressTypes.includes(addressType) ? addressType : 'facturation',
                                    pays: pays || '',
                                    region: region || '',
                                    code_postal: codePostal || '',
                                    adresse: adresse || '',
                                    station_id: this.stationId,
                                    user_id: this.userId,
                                    fournisseur_id: 0
                                };
                            }).filter((addr: any) => addr.type && addr.pays && addr.region && addr.code_postal && addr.adresse)
                            : [];

                        const fournisseur: Fournisseur = {
                            id: 0,
                            identifiant_unique,
                            nom_entreprise,
                            emails: email,
                            civilite,
                            numero_identification_fiscale: numero_identification_fiscale || null,
                            site_web: null,
                            station_id: this.stationId,
                            user_id: this.userId,
                            is_archive: false,
                            telephones: phoneNumbers.map((num: string) => ({
                                id: 0,
                                num_tel: num.replace(/^\+\d{1,3}/, '') || '',
                                code_pays: num.match(/^\+\d{1,3}/)?.[0] || '+216',
                                type: num.includes('+') && this.validPhoneTypes.includes('mobile') ? 'mobile' : 'fixe',
                                station_id: this.stationId,
                                user_id: this.userId,
                                fournisseur_id: 0
                            })),
                            adresses: addresses,
                            created_at: undefined,
                            updated_at: undefined
                        };

                        const sub = this.fournisseurService.addFournisseur(fournisseur).subscribe({
                            next: (addedFournisseur) => {
                                importedCount++;
                                this.fournisseurEventService.emitFournisseurArchiveEvent(addedFournisseur, false);
                                if (importedCount + errors.length === dataRows.length) {
                                    this.finalizeImport(importedCount, errors, modal);
                                }
                            },
                            error: (error) => {
                                const errorMessage = error.error?.errors?.map((err: any) => err.message).join(', ') || `Erreur lors de l'ajout du fournisseur ${fournisseur.nom_entreprise}`;
                                errors.push(`Ligne ${i + 2} : ${errorMessage}`);
                                if (importedCount + errors.length === dataRows.length) {
                                    this.finalizeImport(importedCount, errors, modal);
                                }
                            }
                        });
                        this.subscriptions.push(sub);
                    }

                    if (importedCount + errors.length === 0 && dataRows.length > 0) {
                        this.finalizeImport(importedCount, errors, modal);
                    }
                } catch (error) {
                    if (modal && typeof modal.dismiss === 'function') {
                        modal.dismiss('Error');
                    }
                }
            };

            reader.onerror = () => {
                if (modal && typeof modal.dismiss === 'function') {
                    modal.dismiss('Error');
                }
            };

            reader.readAsArrayBuffer(this.selectedFile);
        } catch (error) {
            if (modal && typeof modal.dismiss === 'function') {
                modal.dismiss('Error');
            }
        }
    }

    private finalizeImport(importedCount: number, errors: string[], modal: any) {
        if (importedCount > 0) {
            this.toastr.success(`Importation réussie : ${importedCount} fournisseur(s) ajouté(s)`, 'Succès');
            this.loadCounts();
        }
        if (errors.length > 0) {
            this.toastr.error(`Erreur(s) lors de l'importation : ${errors.join('; ')}`, 'Erreur');
        }
        this.selectedFile = null;
        if (this.fileInput?.nativeElement) {
            this.fileInput.nativeElement.value = '';
        }
        if (modal && typeof modal.dismiss === 'function') {
            modal.dismiss('Complete');
        } else {
            console.warn('finalizeImport: Modal is undefined or does not have a dismiss method');
        }
    }

    downloadTemplate() {
        try {
            const templateData = [{
                'RÉFÉRENCE': '',
                'NOM DU FOURNISSEUR': '',
                'EMAIL': '',
                'CIVILITÉ': 'Homme,Femme,Entreprise',
                'CHIFFRE D\'AFFAIRES': '',
                'MATRICULE FISCAL': '123456789',
                'NUMÉROS DE TÉLÉPHONE': 'ex: +21674142745;+21612345678',
                'ADRESSES': 'ex: facturation,France,Île-de-France,75001,123 Rue Test|livraison,Belgique,Wallonie,1000,456 Rue Exemple'
            }];
            const worksheet = XLSX.utils.json_to_sheet(templateData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Template');
            XLSX.writeFile(workbook, 'fournisseur_template.xlsx');
            this.toastr.success('Modèle téléchargé', 'Succès');
        } catch (error) {
            console.error('downloadTemplate: Error=', JSON.stringify(error, null, 2));
        }
    }

    exportTable(format: string) {
        const activeSub = this.fournisseurService.getActiveFournisseur(this.stationId, 1, 1000).subscribe({
            next: (response) => {
                const tableData = response.data.map(item => this.mapFournisseurToArchiveItem(item));
                this.performExport(tableData, format);
            },
            error: (error) => {
                console.error('exportTable: Error=', JSON.stringify(error, null, 2));
            }
        });
        this.subscriptions.push(activeSub);
    }

    private mapFournisseurToArchiveItem(fournisseur: Fournisseur): ArchiveItem {
        return {
            reference: fournisseur.identifiant_unique || '',
            fournisseursName: fournisseur.nom_entreprise || '',
            email: fournisseur.emails?.[0]?.email || '',
            gender: fournisseur.civilite || '',
            chiffre_aff: '',
            matricule: fournisseur.numero_identification_fiscale || '',
            phoneNumbers: fournisseur.telephones?.map(tel => `${tel.code_pays}${tel.num_tel}`) || [],
            addresses: fournisseur.adresses?.map(addr => ({
                addressType: addr.type || '',
                pays: addr.pays || '',
                region: addr.region || '',
                codePostal: addr.code_postal || '',
                adresse: addr.adresse || ''
            })) || []
        };
    }

    private performExport(data: ArchiveItem[], format: string) {
        if (!data || data.length === 0) {
            this.toastr.error('Aucune donnée à exporter', 'Erreur');
            return;
        }

        switch (format) {
            case 'pdf':
                this.exportToPDF(data);
                break;
            case 'csv':
                this.exportToCSV(data);
                break;
            case 'xlsx':
                this.exportToXLSX(data);
                break;
            default:
        }
    }

    private exportToPDF(data: ArchiveItem[]) {
        try {
            const doc = new jsPDF();
            autoTable(doc, {
                columns: [

                    { header: 'RÉFÉRENCE', dataKey: 'reference' },
                    { header: 'NOM DU FOURNISSEUR', dataKey: 'fournisseursName' },
                    { header: 'CHIFFRE D\'AFFAIRES', dataKey: 'chiffre_aff' },
                    { header: 'MATRICULE FISCAL', dataKey: 'matricule' }
                ],
                body: data.map(item => ({
                    reference: item.reference || '',
                    fournisseursName: `${item.fournisseursName || ''} (${item.email || ''})`,
                    chiffre_aff: item.chiffre_aff || '',
                    matricule: item.matricule || ''
                })),
                theme: 'striped',
                headStyles: { fillColor: [115, 103, 240] },
                styles: { fontSize: 10 }
            });
            doc.save('fournisseurs.pdf');
            this.toastr.success('Exportation PDF réussie', 'Succès');
        } catch (error) {
            console.error('exportToPDF: Error=', JSON.stringify(error, null, 2));
        }
    }

    private exportToCSV(data: ArchiveItem[]) {
        try {
            const headers = ['RÉFÉRENCE,NOM DU FOURNISSEUR,EMAIL,CIVILITÉ,CHIFFRE D\'AFFAIRES,MATRICULE FISCAL,NUMÉROS DE TÉLÉPHONE,ADRESSES'];
            const rows = data.map(item =>
                `"${item.reference || ''}","${item.fournisseursName || ''}","${item.email || ''}","${item.gender || ''}","${item.chiffre_aff || ''}","${item.matricule || ''}","${(item.phoneNumbers || []).join(';')}","${(item.addresses || []).map(addr => `${addr.addressType},${addr.pays},${addr.region},${addr.codePostal},${addr.adresse}`).join('|')}"`
            );
            const csvContent = headers.concat(rows).join('\n');
            const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', 'fournisseurs.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            this.toastr.success('Exportation CSV réussie', 'Succès');
        } catch (error) {
            console.error('exportToCSV: Error=', JSON.stringify(error, null, 2));
        }
    }

    private exportToXLSX(data: ArchiveItem[]) {
        try {
            const worksheetData = data.map(item => ({
                'RÉFÉRENCE': item.reference || '',
                'NOM DU FOURNISSEUR': item.fournisseursName || '',
                'EMAIL': item.email || '',
                'CIVILITÉ': item.gender || '',
                'CHIFFRE D\'AFFAIRES': item.chiffre_aff || '',
                'MATRICULE FISCAL': item.matricule || '',
                'NUMÉROS DE TÉLÉPHONE': (item.phoneNumbers || []).join(';'),
                'ADRESSES': (item.addresses || []).map(addr => `${addr.addressType},${addr.pays},${addr.region},${addr.codePostal},${addr.adresse}`).join('|')
            }));
            const worksheet = XLSX.utils.json_to_sheet(worksheetData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Fournisseurs');
            XLSX.writeFile(workbook, 'fournisseurs.xlsx');
            this.toastr.success('Exportation XLSX réussie', 'Succès');
        } catch (error) {
            this.toastr.error('Erreur lors de l\'exportation XLSX', 'Erreur');
            console.error('exportToXLSX: Error=', JSON.stringify(error, null, 2));
        }
    }

}
